import React, { useState, useEffect } from 'react';
import { Modal, Switch, Input, Button, message } from 'antd';
import {
    LinkOutlined,
    GlobalOutlined,
    CopyOutlined,
    CheckOutlined,
    StopOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import { updateProject } from './api/projectDashboardApi';

export default function ShareProjectModal({
    open,
    onClose,
    project,
    onProjectUpdated,
}) {
    const [copiedInternal, setCopiedInternal] = useState(false);
    const [copiedPublic, setCopiedPublic] = useState(false);
    const [updating, setUpdating] = useState(false);

    const projectId = project?._id ? String(project._id) : '';
    const [isPublic, setIsPublic] = useState(Boolean(project?.isPublicShared));
    const [publicToken, setPublicToken] = useState(project?.publicShareToken || '');

    useEffect(() => {
        if (project) {
            setIsPublic(Boolean(project.isPublicShared));
            setPublicToken(project.publicShareToken || '');
        }
    }, [project]);

    if (!projectId) return null;

    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    const internalUrl = `${baseUrl}/project-dashboard?projectId=${projectId}&tab=overview`;
    const publicUrl = publicToken
        ? `${baseUrl}/shared/project/${publicToken}`
        : '';

    const handleCopy = (text, type) => {
        if (!text) return;
        if (navigator.clipboard) {
            navigator.clipboard.writeText(text);
        }
        if (type === 'internal') {
            setCopiedInternal(true);
            setTimeout(() => setCopiedInternal(false), 2000);
            message.success('Team link copied to clipboard!');
        } else {
            setCopiedPublic(true);
            setTimeout(() => setCopiedPublic(false), 2000);
            message.success('Public link copied to clipboard!');
        }
    };

    const handleTogglePublic = async (checked) => {
        setUpdating(true);
        const nextToken = checked
            ? (publicToken || Math.random().toString(36).substring(2, 10) + Date.now().toString(36))
            : '';

        // Optimistic UI update
        setIsPublic(checked);
        if (checked) setPublicToken(nextToken);

        try {
            const payload = {
                isPublicShared: checked,
                publicShareToken: checked ? nextToken : null,
            };
            const { data } = await updateProject(projectId, payload);
            if (data?.result) {
                setIsPublic(Boolean(data.result.isPublicShared));
                setPublicToken(data.result.publicShareToken || '');
                onProjectUpdated?.(data.result);
                message.success(checked ? 'Public link enabled' : 'Public link disabled');
            }
        } catch (err) {
            // Revert state on error
            setIsPublic(Boolean(project?.isPublicShared));
            setPublicToken(project?.publicShareToken || '');
            console.error('Failed to update public share settings:', err);
            message.error('Failed to update share settings.');
        } finally {
            setUpdating(false);
        }
    };

    const handleRegenerateToken = async () => {
        setUpdating(true);
        try {
            const nextToken = Math.random().toString(36).substring(2, 10) + Date.now().toString(36);
            const { data } = await updateProject(projectId, {
                isPublicShared: true,
                publicShareToken: nextToken,
            });
            if (data?.result) {
                setIsPublic(true);
                setPublicToken(data.result.publicShareToken || nextToken);
                onProjectUpdated?.(data.result);
                message.success('New public link generated');
            }
        } catch (err) {
            console.error('Failed to regenerate token:', err);
            message.error('Failed to generate new link.');
        } finally {
            setUpdating(false);
        }
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            centered
            width={520}
            title={
                <div className="flex items-center gap-2 text-base font-bold text-slate-800 dark:text-slate-100">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#03488A]" />
                    Share Project
                </div>
            }
        >
            <div className="space-y-4 pt-3 text-xs">
                {/* 1. Internal Workspace Link */}
                <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/40 space-y-2">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 font-bold text-slate-800 dark:text-slate-200 text-xs">
                            <LinkOutlined className="text-[#03488A]" />
                            <span>Internal Team Link</span>
                        </div>
                        <span className="text-[10px] font-semibold text-slate-400 bg-slate-200/70 dark:bg-slate-800 px-2 py-0.5 rounded-md">
                            Team Members
                        </span>
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        Only authenticated team members with project access can view this link.
                    </p>
                    <div className="flex items-center gap-2 pt-0.5">
                        <Input
                            readOnly
                            value={internalUrl}
                            className="bg-white dark:bg-slate-800 text-xs font-mono text-slate-700 dark:text-slate-200"
                        />
                        <Button
                            type="primary"
                            icon={copiedInternal ? <CheckOutlined /> : <CopyOutlined />}
                            onClick={() => handleCopy(internalUrl, 'internal')}
                            className="shrink-0 font-semibold bg-[#03488A] hover:bg-[#023363]"
                        >
                            {copiedInternal ? 'Copied' : 'Copy'}
                        </Button>
                    </div>
                </div>

                {/* 2. Public Read-Only Access */}
                <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/40 space-y-2.5">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 font-bold text-slate-800 dark:text-slate-200 text-xs">
                            <GlobalOutlined className="text-emerald-600" />
                            <span>Public Read-Only Link</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-[11px] font-medium text-slate-500">
                                {isPublic ? 'Enabled' : 'Disabled'}
                            </span>
                            <Switch
                                size="small"
                                checked={isPublic}
                                onChange={handleTogglePublic}
                                loading={updating}
                            />
                        </div>
                    </div>

                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        Anyone with this link can view a read-only overview of this project.
                    </p>

                    {isPublic && publicUrl ? (
                        <div className="space-y-2 pt-1">
                            <div className="flex items-center gap-2">
                                <Input
                                    readOnly
                                    value={publicUrl}
                                    className="bg-white dark:bg-slate-800 text-xs font-mono text-emerald-700 dark:text-emerald-400 font-medium"
                                />
                                <Button
                                    type="default"
                                    icon={copiedPublic ? <CheckOutlined /> : <CopyOutlined />}
                                    onClick={() => handleCopy(publicUrl, 'public')}
                                    className="shrink-0 font-semibold border-emerald-600 text-emerald-700 hover:text-emerald-800 dark:text-emerald-400"
                                >
                                    {copiedPublic ? 'Copied' : 'Copy'}
                                </Button>
                            </div>

                            <div className="flex items-center justify-between pt-1">
                                <Button
                                    type="text"
                                    danger
                                    size="small"
                                    icon={<StopOutlined />}
                                    onClick={() => handleTogglePublic(false)}
                                    loading={updating}
                                    className="text-[11px] p-0 h-auto"
                                >
                                    Revoke Link
                                </Button>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<ReloadOutlined />}
                                    onClick={handleRegenerateToken}
                                    loading={updating}
                                    className="text-[11px] text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 p-0 h-auto"
                                >
                                    Regenerate Link
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="text-[11px] text-slate-400 dark:text-slate-500 italic py-1">
                            Public access is currently turned off. Switch on to generate a link.
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
}
